import React from "react";
import { Target, Layers } from "lucide-react";
import { INDICATORS, findIndicator } from "@/lib/indicators";
import { Field } from "./BuilderPrimitives";


/** Pending orders + anchor + round levels + expiry config panel. */
export function OrderExecutionPanel({ cfg, setCfg, setField }) {
  const setAnchorEnabled = (on) => {
    setCfg((c) => ({
      ...c,
      pending_anchor: on
        ? (c.pending_anchor || { indicator: "BOLLINGER", period: 20, deviation: 2, band: "MODE_LOWER", applied_price: "PRICE_CLOSE" })
        : null,
    }));
  };
  const setAnchor = (patch) => setCfg((c) => {
    const cur = c.pending_anchor || {};
    let next = { ...cur, ...patch };
    if (patch.indicator && patch.indicator !== cur.indicator) {
      const ind = findIndicator(patch.indicator);
      next = { indicator: patch.indicator, applied_price: "PRICE_CLOSE" };
      ind.fields.forEach((f) => { next[f.name] = f.default; });
    }
    return { ...c, pending_anchor: next };
  });

  const showPendingSettings = cfg.buy_order_type !== "market" || cfg.sell_order_type !== "market";
  const anchor = cfg.pending_anchor;
  const anchorInd = anchor ? findIndicator(anchor.indicator) : null;

  return (
    <section className="card-sharp p-5" data-testid="order-execution-panel">
      <div className="flex items-center gap-2 mb-4">
        <Target size={16} className="text-[var(--accent)]"/>
        <h2 className="font-display text-lg text-white">Order Execution</h2>
        <span className="chip">PENDING · LIMIT · STOP</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-tiny">BUY entry type</label>
          <select data-testid="cfg-buy-order-type" className="input-dark" value={cfg.buy_order_type}
            onChange={(e)=>setField("buy_order_type", e.target.value)}>
            <option value="market">Market (OP_BUY)</option>
            <option value="limit">Buy Limit (below price)</option>
            <option value="stop">Buy Stop (above price)</option>
          </select>
        </div>
        <div>
          <label className="label-tiny">SELL entry type</label>
          <select data-testid="cfg-sell-order-type" className="input-dark" value={cfg.sell_order_type}
            onChange={(e)=>setField("sell_order_type", e.target.value)}>
            <option value="market">Market (OP_SELL)</option>
            <option value="limit">Sell Limit (above price)</option>
            <option value="stop">Sell Stop (below price)</option>
          </select>
        </div>
      </div>

      {showPendingSettings && (
        <PendingSettings cfg={cfg} setField={setField}
          setAnchorEnabled={setAnchorEnabled} setAnchor={setAnchor}
          anchor={anchor} anchorInd={anchorInd}/>
      )}
    </section>
  );
}


function PendingSettings({ cfg, setField, setAnchorEnabled, setAnchor, anchor, anchorInd }) {
  return (
    <div className="mt-5 border-t border-white/5 pt-5 space-y-4" data-testid="order-pending-settings">
      <p className="text-xs text-[var(--text-secondary)]">
        Pending orders are placed at <span className="font-mono text-white">anchor ± offset</span> pips.
        Optionally snap to a psychological/round-number step, and auto-cancel after N minutes.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Field label="Offset pips (from anchor / market)">
          <input data-testid="cfg-pending-offset" type="number" className="input-dark"
            value={cfg.pending_offset_pips}
            onChange={(e)=>setField("pending_offset_pips", parseInt(e.target.value)||0)}/>
        </Field>
        <Field label="Round to nearest N pips (0=off)">
          <input data-testid="cfg-round-step" type="number" className="input-dark"
            value={cfg.pending_round_step_pips}
            onChange={(e)=>setField("pending_round_step_pips", parseInt(e.target.value)||0)}/>
        </Field>
        <Field label="Auto-expire minutes (0=never)">
          <input data-testid="cfg-expire-min" type="number" className="input-dark"
            value={cfg.pending_expire_minutes}
            onChange={(e)=>setField("pending_expire_minutes", parseInt(e.target.value)||0)}/>
        </Field>
      </div>

      <OcoToggle cfg={cfg} setField={setField}/>
      <AnchorSection anchor={anchor} anchorInd={anchorInd}
        setAnchorEnabled={setAnchorEnabled} setAnchor={setAnchor}/>
    </div>
  );
}


function OcoToggle({ cfg, setField }) {
  return (
    <div className="border border-white/10 rounded p-3 flex items-center gap-3">
      <input data-testid="cfg-oco-toggle" type="checkbox" id="oco-toggle"
        checked={!!cfg.oco_enabled}
        onChange={(e)=>setField("oco_enabled", e.target.checked)}
        className="accent-[var(--accent)]"/>
      <label htmlFor="oco-toggle" className="cursor-pointer">
        <div className="font-mono text-xs uppercase tracking-widest text-[var(--text-primary)]">
          OCO Bracket — One-Cancels-The-Other
        </div>
        <div className="text-xs text-[var(--text-secondary)] mt-0.5">
          On any entry signal, place BOTH pending sides. When one fills, the opposite is auto-cancelled.
          Classic breakout pattern (Buy Stop above + Sell Stop below).
        </div>
      </label>
    </div>
  );
}


function AnchorSection({ anchor, anchorInd, setAnchorEnabled, setAnchor }) {
  return (
    <div className="border border-white/10 rounded p-3">
      <label className="flex items-center gap-2 cursor-pointer">
        <input data-testid="cfg-anchor-toggle" type="checkbox"
          checked={!!anchor}
          onChange={(e)=>setAnchorEnabled(e.target.checked)}
          className="accent-[var(--accent)]"/>
        <span className="font-mono text-xs uppercase tracking-widest text-[var(--text-secondary)]">
          Anchor entry price to indicator level
        </span>
        <Layers size={12} className="text-[var(--text-muted)]"/>
      </label>

      {anchor && anchorInd && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cfg-anchor-fields">
          <Field label="Anchor indicator">
            <select data-testid="cfg-anchor-indicator" className="input-dark"
              value={anchor.indicator}
              onChange={(e)=>setAnchor({indicator:e.target.value})}>
              {INDICATORS.map(i => <option key={i.id} value={i.id}>{i.label}</option>)}
            </select>
          </Field>
          {anchorInd.fields.map((f) => (
            <Field key={f.name} label={f.label}>
              {f.type === "select" ? (
                <select className="input-dark" data-testid={`cfg-anchor-${f.name}`}
                  value={anchor[f.name] ?? f.default}
                  onChange={(e)=>setAnchor({[f.name]: e.target.value})}>
                  {f.options.map(o => <option key={o} value={o}>{o.replace("MODE_","")}</option>)}
                </select>
              ) : (
                <input type="number" className="input-dark" data-testid={`cfg-anchor-${f.name}`}
                  value={anchor[f.name] ?? f.default}
                  onChange={(e)=>setAnchor({[f.name]: parseFloat(e.target.value)||0})}/>
              )}
            </Field>
          ))}
        </div>
      )}
    </div>
  );
}
